<script>
import Head from '@/components/Head';
import Button from '@/components/Button';
import Loader from '@/components/Loader';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

export default {
    data() {
        return {
            loader: false,
            canGenerate: false,
            alreadyGenerated: false,
            currentYear: new Date().getFullYear(),
            targetYear: new Date().getFullYear(),
            currentMonth: new Date().getMonth() + 1,
            totalAssociates: 0,
            generatedCount: 0,
        }
    },
    components: {
        Head,
        Button,
        Loader,
    },
    created() {
        this.checkIfCanGenerate();
    },
    methods: {
        notify(text, type) {
            toast(text, {
                "type": type == 'info' ? 'info' : type == 'warning' ? 'warning' : type == 'error' ? 'error' : type == 'success' ? 'success' : 'default',
            });
        },
        checkIfCanGenerate() {
            this.loader = true;

            this.$axios.get('/installments/check_generation')
            .then(res => {
                const data = res.data.data;
                
                this.canGenerate = data.can_generate;
                this.alreadyGenerated = data.already_generated;
                this.totalAssociates = data.total_associates;
                this.targetYear = data.target_year || this.currentYear;
                
                this.loader = false;
            })
            .catch(err => {
                this.notify('Erro ao verificar status de geração!', 'error');
                this.loader = false;
            });
        },
        generateInstallments() {
            if (!this.canGenerate) {
                this.notify('Geração de parcelas só pode ser feita em janeiro ou dezembro!', 'warning');
                return;
            }

            if (this.alreadyGenerated) {
                this.notify(`As parcelas de ${this.targetYear} já foram geradas!`, 'warning');
                return;
            }

            this.loader = true;

            this.$axios.post('/installments/generate')
            .then(res => {
                const data = res.data.data;
                
                this.generatedCount = data.generated_count;
                this.alreadyGenerated = true;
                this.targetYear = data.year;
                
                this.notify(`${data.generated_count} parcelas geradas com sucesso para ${data.associates_count} associados do ano ${data.year}!`, 'success');
                
                this.loader = false;
            })
            .catch(err => {
                this.notify(err.response?.data?.message || 'Erro ao gerar parcelas!', 'error');
                this.loader = false;
            });
        },
    }
}
</script>

<template>
    <section>
        <Loader v-if="loader" />

        <Head title="Gerar Parcelas Anuais" />

        <div class="generate-installments-container">
            <div class="info-card">
                <h2>Gerar Parcelas Anuais - Ano {{ targetYear }}</h2>
                
                <div class="info-section">
                    <p><strong>Mês atual:</strong> 
                        <span v-if="currentMonth == 1">Janeiro (Permitido)</span>
                        <span v-else-if="currentMonth == 12">Dezembro (Permitido - Gera ano seguinte)</span>
                        <span v-else>Mês {{ currentMonth }} (Bloqueado)</span>
                    </p>
                    <p><strong>Ano de geração:</strong> {{ targetYear }}</p>
                    <p><strong>Total de associados:</strong> {{ totalAssociates }}</p>
                    <p><strong>Status:</strong> 
                        <span v-if="alreadyGenerated" class="status-generated">Parcelas de {{ targetYear }} já geradas</span>
                        <span v-else-if="canGenerate" class="status-pending">Aguardando geração</span>
                        <span v-else class="status-blocked">Bloqueado (somente em janeiro ou dezembro)</span>
                    </p>
                </div>

                <div class="warning-section" v-if="canGenerate && !alreadyGenerated">
                    <div class="warning-box">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="icon-warning">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>
                        <div>
                            <h3>Atenção!</h3>
                            <p>Esta ação irá gerar 12 parcelas mensais para TODOS os associados do ano {{ targetYear }}.</p>
                            <p v-if="currentMonth == 12"><strong>Gerando em dezembro:</strong> As parcelas serão criadas para o ano seguinte ({{ targetYear }}).</p>
                            <p>Cada parcela terá vencimento no dia 10 de cada mês.</p>
                            <p>Esta operação só pode ser executada UMA VEZ por ano.</p>
                        </div>
                    </div>
                </div>

                <div class="action-section">
                    <Button 
                        v-if="canGenerate && !alreadyGenerated" 
                        type="primary" 
                        @buttonPressed="generateInstallments" 
                        placeholder="Gerar Parcelas do Ano" 
                    />
                    <Button 
                        v-else 
                        type="tertiary" 
                        placeholder="Geração Bloqueada" 
                        :disabled="true"
                    />
                </div>

                <div v-if="generatedCount > 0" class="success-section">
                    <p class="success-message">✓ {{ generatedCount }} parcelas geradas com sucesso!</p>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.generate-installments-container {
    max-width: 800px;
    margin: 40px auto;
    padding: 0 20px;
}

.info-card {
    background: white;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.info-card h2 {
    margin-bottom: 25px;
    color: #333;
    font-size: 24px;
}

.info-section {
    margin-bottom: 25px;
}

.info-section p {
    margin: 10px 0;
    font-size: 16px;
    color: #555;
}

.status-generated {
    color: #27ae60;
    font-weight: bold;
}

.status-pending {
    color: #f39c12;
    font-weight: bold;
}

.status-blocked {
    color: #e74c3c;
    font-weight: bold;
}

.warning-section {
    margin: 25px 0;
}

.warning-box {
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    padding: 20px;
    display: flex;
    gap: 15px;
}

.icon-warning {
    width: 40px;
    height: 40px;
    color: #ff9800;
    flex-shrink: 0;
}

.warning-box h3 {
    margin: 0 0 10px 0;
    color: #856404;
}

.warning-box p {
    margin: 5px 0;
    color: #856404;
    font-size: 14px;
}

.action-section {
    margin-top: 30px;
    display: flex;
    justify-content: center;
}

.success-section {
    margin-top: 20px;
    text-align: center;
}

.success-message {
    color: #27ae60;
    font-size: 18px;
    font-weight: bold;
}
</style>
